/**
 * The Devory keycap legends — clean, single-weight outline icons in the
 * spirit of a premium physical keyset, engraved into the cap rather than
 * sitting on top of it as a filled sticker. One stroke width, round caps and
 * joins, `currentColor` throughout so the surrounding key supplies the
 * accent — that consistency is what makes a set of otherwise-unrelated
 * glyphs read as one keyset. See the 2026-07-17 keycap material pass.
 */

const STROKE = 1.8;

export type KeycapIconName =
  | "rocket"
  | "link"
  | "cube"
  | "shield-check"
  | "lock"
  | "workspace"
  | "runtime"
  | "deployment"
  | "git"
  | "terminal"
  | "ai"
  | "extension"
  | "identity"
  | "cloud";

export interface KeycapIconProps {
  className?: string;
}

export function RocketIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M15 8c0-3-1.4-5-3-6-1.6 1-3 3-3 6v7h6z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
      <circle cx="12" cy="8.4" r="1.3" fill="none" stroke="currentColor" strokeWidth={STROKE} />
      <path
        d="M9 13.2l-3.2 3.8 4.2-1.4"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 13.2l3.2 3.8-4.2-1.4"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15c-1.1 1.6-1.1 3.2 0 4.8 1.1-1.6 1.1-3.2 0-4.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LinkIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M10 16.5a4.2 4.2 0 0 1 0-6l1.8-1.8a4.2 4.2 0 0 1 6 6l-.9.9"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
      <path
        d="M14 7.5a4.2 4.2 0 0 1 0 6l-1.8 1.8a4.2 4.2 0 0 1-6-6l.9-.9"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CubeIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M12 3.5 19 7.3 19 14.9 12 18.7 5 14.9 5 7.3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
      <path
        d="M12 3.5V11.1M5 14.9 12 11.1 19 14.9"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ShieldCheckIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M12 2.6l6.4 2.6v5.4c0 4.6-2.7 7.5-6.4 8.6-3.7-1.1-6.4-4-6.4-8.6V5.2z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
      <path
        d="M8.6 12.1l2.5 2.5 4.3-4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LockIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M8.3 10.5V8a3.7 3.7 0 0 1 7.4 0v2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
      <rect
        x="5.8"
        y="10.5"
        width="12.4"
        height="9"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <circle cx="12" cy="14.6" r="1.1" fill="currentColor" />
      <path d="M12 15.7v1.4" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
    </svg>
  );
}

export function WorkspaceIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect
        x="4"
        y="4"
        width="16"
        height="5.5"
        rx="1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <rect
        x="4"
        y="11.5"
        width="7.2"
        height="8.5"
        rx="1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <rect
        x="12.8"
        y="11.5"
        width="7.2"
        height="8.5"
        rx="1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
    </svg>
  );
}

export function RuntimeIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M7 4.5v15l13-7.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function DeploymentIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M7 10.5 12 5l5 5.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 7v8.5" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
      <path d="M5 18h14" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
    </svg>
  );
}

export function GitIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path d="M6.2 8.7v6.6" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
      <path d="M7.8 9.3l8 2.3" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
      <circle cx="6.2" cy="6.4" r="2" fill="none" stroke="currentColor" strokeWidth={STROKE} />
      <circle cx="6.2" cy="17.6" r="2" fill="none" stroke="currentColor" strokeWidth={STROKE} />
      <circle cx="18" cy="11" r="2" fill="none" stroke="currentColor" strokeWidth={STROKE} />
    </svg>
  );
}

export function TerminalIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2.4"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <path
        d="M7 10l3.2 2.6L7 15.2"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13 14.3h4.2" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
    </svg>
  );
}

export function AiIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
      <path
        d="M18 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ExtensionIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect
        x="7"
        y="9"
        width="10"
        height="8.5"
        rx="2.2"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
      />
      <path
        d="M9.7 4v6M14.3 4v6"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
      <path d="M12 17.5v2.8" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
    </svg>
  );
}

export function IdentityIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <circle cx="12" cy="8.2" r="3.4" fill="none" stroke="currentColor" strokeWidth={STROKE} />
      <path
        d="M5 20c0-4.5 3.2-7 7-7s7 2.5 7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CloudIcon({ className }: KeycapIconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M7.2 17.3a4.1 4.1 0 0 1 .3-8.2 5.6 5.6 0 0 1 10.6-1.4 3.9 3.9 0 0 1-.5 9.6z"
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinejoin="round"
      />
    </svg>
  );
}
